import type { ProjectDetail } from "@esi/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { api } from "../api.ts";
import { ApiError, ok } from "../api-errors.ts";
import { useMe } from "../me.ts";
import { can, canChange } from "../projects/capabilities.ts";
import { useDocuments, useKnowledgeSettings, useMembers } from "../projects/queries.ts";
import { memberName } from "../projects/readers.ts";
import { fileMediaType } from "../projects/upload.ts";
import {
  conversationPath,
  HOME_PATH,
  Link,
  navigate,
  openArtefactOf,
  withOpenArtefact,
} from "../router.tsx";
import { useSession } from "../session.tsx";
import { conversationAudience } from "../trust/audience.ts";
import { AttachmentChip } from "./attachments/AttachmentControls.tsx";
import { AttachmentWorkspace } from "./attachments/AttachmentWorkspace.tsx";
import { useAttachmentDraft } from "./attachments/queries.ts";
import { ComposerFrame } from "./Composer.tsx";
import { ConversationHeader } from "./ConversationHeader.tsx";
import { useChatCache } from "./chat-cache.tsx";
import { CitationSchemeContext } from "./classification.tsx";
import { StopProjectShareAction } from "./ProjectShareSection.tsx";
import { conversationKeys, refreshConversations, useConversation } from "./queries.ts";
import { ShareDialog } from "./ShareDialog.tsx";
import { chatMessagesOf, Thread } from "./Thread.tsx";

/**
 * One conversation at its own URL (design §7): the URL is the selection. Its header, then its
 * thread and composer. A project conversation shows inside its project (`project`); a standalone
 * one alone, with no knowledge. A conversation opened at the other kind's URL (moved, linked by
 * ID) is redirected to its own, replacing the history entry. Any refusal reads as not found.
 */
export function ConversationView(props: {
  conversationId: string;
  /** The project of the URL, for a project conversation. */
  project?: ProjectDetail;
  /** Shown in the header before the actions (the knowledge panel's toggle). */
  headerExtra?: ReactNode;
}) {
  const { conversationId, project } = props;
  const { t } = useTranslation();
  const detail = useConversation(conversationId);
  const cache = useChatCache();
  // A chat started or still streaming here is shown at once, without waiting for the history.
  const [kept] = useState(() => cache.take(conversationId) !== undefined);
  const data = detail.data;
  const elsewhere =
    data !== undefined && (data.projectId ?? null) !== (project?.id.toLowerCase() ?? null);
  useEffect(() => {
    if (data && elsewhere) {
      // An open artefact (`?artefact=&v=`) goes along.
      navigate(withOpenArtefact(conversationPath(data), openArtefactOf(window.location.search)), {
        replace: true,
      });
    }
  }, [data, elsewhere]);
  useDocumentTitle(data?.title ?? (data ? t("conversation.untitled") : undefined));

  // A refusal is authoritative: a conversation opened before (still cached) is not shown again
  // once the caller lost access to it.
  const refused =
    detail.error instanceof ApiError &&
    (detail.error.status === 404 || detail.error.status === 403);
  if (detail.isError && (!data || refused)) {
    const missing = detail.error instanceof ApiError && detail.error.status === 404;
    return (
      <div className="px-4 py-6 sm:px-6">
        <Notice
          tone={missing ? "neutral" : "danger"}
          title={missing ? t("conversationView.notFoundTitle") : undefined}
          action={
            <Button asChild size="sm" variant="outline">
              <Link href={HOME_PATH}>{t("conversationView.backHome")}</Link>
            </Button>
          }
        >
          {missing ? t("conversationView.notFound") : detail.error.message}
        </Notice>
      </div>
    );
  }
  if (!data || elsewhere || !(detail.isFetchedAfterMount || kept)) {
    return (
      <LoadingConversation conversationId={conversationId} standalone={project === undefined} />
    );
  }
  return (
    <AttachmentWorkspace conversationId={conversationId}>
      <OpenConversation
        key={conversationId}
        detail={data}
        project={project}
        headerExtra={props.headerExtra}
      />
    </AttachmentWorkspace>
  );
}

/**
 * The thread's placeholder above an inert copy of the pinned composer, holding the files handed
 * over from Home: the composer moves once, into place, and stays there when the thread appears.
 */
function LoadingConversation(props: { conversationId: string; standalone: boolean }) {
  const { t } = useTranslation();
  const { state } = useAttachmentDraft(props.conversationId);
  const toReview = state.files.some((f) => f.phase === "review");
  return (
    <div className="flex min-h-0 flex-1 flex-col" aria-busy="true">
      <div className="min-h-0 flex-1">
        <div className="mx-auto flex w-full max-w-3xl flex-col gap-3 px-4 py-6">
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="ml-auto h-10 w-1/2 rounded-2xl" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
        </div>
      </div>
      <div
        className="mx-auto flex w-full max-w-3xl flex-col gap-2 px-3 pb-4 sm:px-4"
        aria-hidden="true"
        inert
        data-composer-placeholder=""
      >
        {/* Same frame and rhythm as the composer, without its named controls. */}
        <ComposerFrame onSubmit={(e) => e.preventDefault()}>
          {state.files.length > 0 ? (
            <div className="flex flex-col gap-2 px-4 pt-3">
              <div className="flex h-6 items-center text-xs font-medium text-fg-secondary tabular-nums">
                {t("attachments.count", { count: state.files.length })}
              </div>
              <ul className="flex min-w-0 flex-wrap gap-1.5">
                {state.files.map((f) => (
                  <AttachmentChip
                    key={f.key}
                    title={f.title}
                    mediaType={f.file ? (fileMediaType(f.file) ?? "") : ""}
                    local={f}
                  />
                ))}
              </ul>
            </div>
          ) : null}
          <div className="min-h-14 px-5 pt-4 pb-1 text-[15px] leading-relaxed whitespace-pre-wrap text-fg-muted">
            {state.text || t(props.standalone ? "standalone.ask" : "conversation.ask")}
          </div>
          <div className="h-12" />
          {toReview ? (
            <div className="flex min-w-0 items-center gap-2 px-5 pb-3">
              <p className="min-w-0 flex-1 text-xs leading-6 text-fg-secondary">
                {t("attachments.reviewHint")}
              </p>
              <span className="px-2 text-xs font-medium text-fg">{t("attachments.review")}</span>
            </div>
          ) : null}
        </ComposerFrame>
        <div className="h-4" />
      </div>
    </div>
  );
}

function OpenConversation(props: {
  detail: NonNullable<ReturnType<typeof useConversation>["data"]>;
  project: ProjectDetail | undefined;
  headerExtra?: ReactNode;
}) {
  const { detail, project } = props;
  const { t } = useTranslation();
  const { userId } = useSession();
  const me = useMe();
  const queryClient = useQueryClient();
  const id = detail.id;
  // One array per detail: a re-render alone does not read as a new authorized history (#207).
  const initialMessages = useMemo(() => chatMessagesOf(detail), [detail]);
  const owned = detail.createdBy.toLowerCase() === userId.toLowerCase();
  // Guests see neither the member list nor the project's settings (the server refuses both).
  const insider = project ? can(project, "project.view_members") : false;
  const members = useMembers(project?.id ?? "", insider);
  const settings = useKnowledgeSettings(project?.id ?? "", insider);
  const documents = useDocuments(project?.id ?? "", project !== undefined);
  const shares = useQuery({
    queryKey: conversationKeys.shares(id),
    // The owner's own list; a recipient learns who shared it from the conversation itself.
    enabled: owned,
    queryFn: async () =>
      ok(
        await api.api.conversations[":conversationId"].shares.$get({
          param: { conversationId: id },
        }),
      ),
  });
  const [sharing, setSharing] = useState(false);
  // Whether an answer of the thread used web results (said in the header and share dialog).
  const [webResults, setWebResults] = useState(false);
  const [ranCode, setRanCode] = useState(false);
  const frozen = project?.frozenAt != null;
  const sharerName =
    detail.sharedBy?.name ??
    (detail.sharedBy ? memberName(members.data, detail.sharedBy.userId) : null) ??
    memberName(members.data, detail.createdBy);
  const audience = owned
    ? shares.data
      ? {
          text: conversationAudience({
            owned: true,
            shares: shares.data.length,
            project: detail.projectShare != null,
          }),
          // Shared with the project: readable beyond the owner, so no lock.
          restricted: shares.data.length === 0 && detail.projectShare == null,
        }
      : null
    : {
        text: conversationAudience({
          owned: false,
          ownerName: sharerName,
          project: detail.projectShare != null,
        }),
        restricted: true,
      };
  const standaloneOff = project === undefined && me.data?.standaloneAllowed === false;
  const readOnly = frozen
    ? t("conversation.frozen")
    : !owned
      ? detail.projectShare != null
        ? t("conversation.projectSharedReadOnly", { name: sharerName })
        : t("conversation.sharedReadOnly", { name: sharerName })
      : standaloneOff
        ? t("standalone.disabled")
        : null;
  // The document list carries the scheme, for guests too (who read no settings).
  const scheme = settings.data?.scheme ?? documents.data?.classifications;
  // A document opens per click only where its classification allows downloads.
  const canOpen = (documentId: string) => {
    const document = documents.data?.documents.find((d) => d.id === documentId);
    const level = scheme?.find((l) => l.id === document?.classification);
    return (
      document !== undefined && document.status !== "uploading" && level?.downloadAllowed === true
    );
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <ConversationHeader
        conversation={detail}
        owned={owned}
        // On a frozen project the dialog still opens, read-only: shares and the project share show.
        canShare={project ? can(project, "conversation.share") : true}
        canRename={!frozen}
        audience={audience}
        webResults={webResults}
        onShare={() => setSharing(true)}
        extra={
          <>
            {props.headerExtra}
            {/* An owner or manager may stop someone else's project share (#170). */}
            {!owned &&
            detail.projectShare != null &&
            project &&
            canChange(project, "members.manage") ? (
              <StopProjectShareAction conversationId={id} projectId={project.id} />
            ) : null}
          </>
        }
      />
      <CitationSchemeContext value={scheme}>
        <Thread
          projectId={project?.id ?? null}
          conversationId={id}
          initialMessages={initialMessages}
          readOnly={readOnly}
          // Reached only through the project share: read-only, nothing is saved from it.
          canSaveFile={
            project ? canChange(project, "file.save_answer") && !detail.viaProjectShare : owned
          }
          canSaveWiki={
            project ? canChange(project, "wiki.draft") && !detail.viaProjectShare : owned
          }
          canOpen={canOpen}
          settings={settings.data}
          audience={audience}
          residency={project ? project.residency?.value : me.data?.residency?.value}
          onTurnEnd={() => refreshConversations(queryClient, id)}
          onWebResults={setWebResults}
          onRanCode={setRanCode}
        />
      </CitationSchemeContext>
      {owned ? (
        <ShareDialog
          open={sharing}
          onClose={() => setSharing(false)}
          projectId={project?.id ?? null}
          conversationId={id}
          members={members.data}
          canManageMembers={project ? canChange(project, "members.manage") : false}
          webResults={webResults}
          ranCode={ranCode}
          projectShare={detail.projectShare}
          frozen={frozen}
        />
      ) : null}
    </div>
  );
}
